import React, { useMemo, useState } from 'react';
import { TbList, TbLock, TbPlus, TbTrash, TbWorld } from 'react-icons/tb';
import ConfirmModal from '../../../../components/ConfirmModal';
import {
  ItemList,
  Item,
  ItemIcon,
  ItemRow,
  SectionHeader,
  Sidebar,
  SidebarBody,
  SidebarHeader,
  SidebarSearch,
  SidebarSection
} from './style';
import type { LookupDictionary } from './types';

interface Props {
  idPrefix?: string;
  general: LookupDictionary[];
  custom: LookupDictionary[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onAdd: () => void;
  onDelete?: (id: string) => void | Promise<unknown>;
  isLoading?: boolean;
  isDeleting?: boolean;
}

const LookupSidebar: React.FC<Props> = ({
  idPrefix,
  general,
  custom,
  selectedId,
  onSelect,
  onAdd,
  onDelete,
  isLoading,
  isDeleting
}) => {
  const rootId = idPrefix || 'lookup-tables-sidebar';
  const [search, setSearch] = useState('');
  const [pendingDelete, setPendingDelete] = useState<LookupDictionary | null>(null);

  const filterByName = (list: LookupDictionary[]) => {
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(d => d.name.toLowerCase().includes(q));
  };

  const filteredGeneral = useMemo(() => filterByName(general), [general, search]);
  const filteredCustom = useMemo(() => filterByName(custom), [custom, search]);

  return (
    <Sidebar id={`${rootId}-root`}>
      <SidebarHeader id={`${rootId}-header`}>
        <span id={`${rootId}-title`}>Lookup Library</span>
        <button
          id={`${rootId}-add-button`}
          type="button"
          aria-label="Add custom dictionary"
          onClick={onAdd}
          style={{
            border: 'none',
            background: 'transparent',
            color: '#228be6',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center'
          }}
        >
          <TbPlus size={18} />
        </button>
      </SidebarHeader>

      <SidebarSearch id={`${rootId}-search`}>
        <input
          id={`${rootId}-search-input`}
          type="text"
          placeholder="Search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          aria-label="Search dictionaries"
        />
      </SidebarSearch>

      <SidebarBody id={`${rootId}-body`}>
        <SidebarSection id={`${rootId}-general-section`}>
          <SectionHeader id={`${rootId}-general-header`}>
            <span className="left" id={`${rootId}-general-header-left`}>
              <TbWorld size={12} />
              General Dictionaries
            </span>
          </SectionHeader>
          <ItemList id={`${rootId}-general-list`}>
            {filteredGeneral.map(d => {
              const active = !!d.id && d.id === selectedId;
              return (
                <Item
                  id={`${rootId}-general-item-${d.id}`}
                  key={d.id}
                  $active={active}
                  onClick={() => onSelect(d.id)}
                  aria-current={active ? 'true' : undefined}
                >
                  <ItemIcon id={`${rootId}-general-item-icon-${d.id}`} $active={active}>
                    <TbList size={12} />
                  </ItemIcon>
                  <span
                    id={`${rootId}-general-item-name-${d.id}`}
                    style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {d.name}
                  </span>
                </Item>
              );
            })}
            {filteredGeneral.length === 0 && search && (
              <Item
                id={`${rootId}-general-empty`}
                as="li"
                $active={false}
                style={{ color: '#adb5bd' }}
              >
                No matches
              </Item>
            )}
          </ItemList>
        </SidebarSection>

        <SidebarSection id={`${rootId}-custom-section`}>
          <SectionHeader id={`${rootId}-custom-header`}>
            <span className="left" id={`${rootId}-custom-header-left`}>
              <TbLock size={12} />
              Custom
            </span>
          </SectionHeader>
          <ItemList id={`${rootId}-custom-list`}>
            {filteredCustom.map(d => {
              const active = !!d.id && d.id === selectedId;
              return (
                <Item
                  id={`${rootId}-custom-item-${d.id}`}
                  key={d.id}
                  $active={active}
                  onClick={() => onSelect(d.id)}
                  aria-current={active ? 'true' : undefined}
                >
                  <ItemIcon id={`${rootId}-custom-item-icon-${d.id}`} $active={active}>
                    <TbList size={12} />
                  </ItemIcon>
                  <ItemRow id={`${rootId}-custom-item-row-${d.id}`} style={{ flex: 1 }}>
                    <span
                      id={`${rootId}-custom-item-name-${d.id}`}
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {d.name}
                    </span>
                    {onDelete && active && (
                      <button
                        id={`${rootId}-custom-delete-${d.id}`}
                        type="button"
                        aria-label={`Delete ${d.name}`}
                        onClick={e => {
                          e.stopPropagation();
                          setPendingDelete(d);
                        }}
                        className="delete"
                      >
                        <TbTrash size={14} />
                      </button>
                    )}
                  </ItemRow>
                </Item>
              );
            })}
            {filteredCustom.length === 0 && !isLoading && (
              <Item id={`${rootId}-custom-empty`} as="li" $active={false}>
                <span id={`${rootId}-custom-empty-text`} style={{ color: '#adb5bd' }}>
                  {search ? 'No matches' : 'No custom dictionaries yet'}
                </span>
              </Item>
            )}
            {isLoading && filteredCustom.length === 0 && (
              <Item id={`${rootId}-custom-loading`} as="li" $active={false}>
                <span id={`${rootId}-custom-loading-text`} style={{ color: '#adb5bd' }}>
                  Loading…
                </span>
              </Item>
            )}
          </ItemList>
        </SidebarSection>
      </SidebarBody>

      <ConfirmModal
        id={`${rootId}-delete-modal`}
        isOpen={!!pendingDelete}
        title="Delete dictionary"
        description={
          pendingDelete
            ? `Are you sure you want to delete "${pendingDelete.name}"? This action cannot be undone.`
            : ''
        }
        confirmButtonTitle="Delete"
        colorSchema="red"
        isLoading={isDeleting}
        onClose={() => {
          // Block dismissing the modal mid-request so the loading state on the
          // confirm button stays visible until the delete resolves.
          if (isDeleting) return;
          setPendingDelete(null);
        }}
        onConfirm={async () => {
          if (!pendingDelete || !onDelete) return;
          try {
            await onDelete(pendingDelete.id);
          } finally {
            setPendingDelete(null);
          }
        }}
      />
    </Sidebar>
  );
};

export default LookupSidebar;
